// src/features/merchantConfig/components/GlobalConfigSearch.jsx
//
// Header search input + results dropdown (UI prompt §4 + §6).
//
// Behaviour:
//   * Search runs against ALREADY-loaded category data; never fires GETs.
//   * Footer of the dropdown reports "Searching N of 12 loaded" when some
//     probes haven't resolved.
//   * Results grouped by category. Clicking a result invokes onPickResult
//     with the category slug; the parent uses that to navigate.
//   * Press `/` anywhere to focus the input. Esc closes the dropdown.
//   * Debounced 150ms - fast enough to feel live, slow enough to skip noise.

import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Search, X } from "lucide-react";
import { useDebounce } from "use-debounce";

/* ---------- Highlight inside a result row ---------- */

function HighlightedText({ text, highlights }) {
  if (!highlights || highlights.length === 0) return <>{text}</>;
  const sorted = [...highlights].sort((a, b) => a.start - b.start);
  const pieces = [];
  let cursor = 0;
  for (let i = 0; i < sorted.length; i += 1) {
    const { start, end } = sorted[i];
    if (start > cursor) pieces.push({ text: text.slice(cursor, start), hit: false });
    pieces.push({ text: text.slice(start, end), hit: true });
    cursor = end;
  }
  if (cursor < text.length) pieces.push({ text: text.slice(cursor), hit: false });
  return (
    <>
      {pieces.map((p, i) =>
        p.hit ? (
          <mark key={i} className="rounded bg-yellow-200 px-0.5 text-gray-900">
            {p.text}
          </mark>
        ) : (
          <React.Fragment key={i}>{p.text}</React.Fragment>
        )
      )}
    </>
  );
}

/* ---------- Main component ---------- */

/**
 * @param {{
 *   inputValue: string,
 *   onInputChange: (value: string) => void,
 *   searchState: ReturnType<typeof import('../hooks/useConfigSearch').useConfigSearch>,
 *   onPickResult: (categorySlug: string) => void,
 * }} props
 */
export default function GlobalConfigSearch({
  inputValue,
  onInputChange,
  searchState,
  onPickResult,
}) {
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [debouncedValue] = useDebounce(inputValue, 150);

  // Global `/` shortcut focuses the input (skip when user is typing into
  // another text field).
  useEffect(() => {
    const handler = (e) => {
      if (e.key !== "/") return;
      const target = e.target;
      const isEditing =
        target &&
        ((target.tagName === "INPUT" && target.type !== "checkbox") ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable);
      if (isEditing) return;
      e.preventDefault();
      inputRef.current?.focus();
      setOpen(true);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Close on click outside.
  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const onKeyDown = useCallback((e) => {
    if (e.key === "Escape") {
      setOpen(false);
      inputRef.current?.blur();
    }
  }, []);

  const onChange = useCallback(
    (e) => {
      onInputChange(e.target.value);
      setOpen(true);
    },
    [onInputChange]
  );

  const onClear = useCallback(() => {
    onInputChange("");
    inputRef.current?.focus();
  }, [onInputChange]);

  const showDropdown = useMemo(() => {
    return open && debouncedValue.trim().length >= 2;
  }, [open, debouncedValue]);

  const { groups, totalMatches, loadedCount, totalCount, enabled } = searchState;

  return (
    <div ref={wrapperRef} className="relative w-full max-w-md">
      <label htmlFor="merchant-config-search" className="sr-only">
        Search configurations
      </label>
      <div className="relative">
        <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
          <Search className="h-4 w-4" aria-hidden="true" />
        </span>
        <input
          ref={inputRef}
          id="merchant-config-search"
          type="search"
          autoComplete="off"
          spellCheck={false}
          placeholder="Search all configurations  ( / )"
          value={inputValue}
          onChange={onChange}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          aria-expanded={showDropdown}
          aria-haspopup="listbox"
          className="w-full rounded-lg border border-gray-200 bg-white py-2.5 pl-9 pr-9 text-sm shadow-sm placeholder:text-gray-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
        />
        {inputValue && (
          <button
            type="button"
            onClick={onClear}
            aria-label="Clear search"
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>

      {showDropdown && (
        <div
          role="listbox"
          aria-label="Search results"
          className="absolute left-0 right-0 z-30 mt-2 max-h-[480px] overflow-auto rounded-lg border border-gray-200 bg-white shadow-xl"
        >
          {!enabled ? (
            <div className="px-4 py-3 text-sm text-gray-500">
              Type at least 2 characters to search.
            </div>
          ) : totalMatches === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500">
              No matches in {loadedCount} loaded {loadedCount === 1 ? "category" : "categories"}.
            </div>
          ) : (
            <>
              <ul className="divide-y divide-gray-100">
                {groups.map((group) => (
                  <li key={group.categorySlug}>
                    <div className="bg-gray-50 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                      {group.categoryDisplayName}
                      <span className="ml-2 font-normal text-gray-400">
                        ({group.matches.length})
                      </span>
                    </div>
                    <ul>
                      {group.matches.map((m) => (
                        <li key={m.pathKey}>
                          <button
                            type="button"
                            role="option"
                            aria-selected="false"
                            onClick={() => {
                              onPickResult(m.categorySlug);
                              setOpen(false);
                            }}
                            className="flex w-full flex-col items-start gap-0.5 px-4 py-2.5 text-left hover:bg-blue-50 focus:bg-blue-50 focus:outline-none"
                          >
                            <span className="text-sm font-medium text-gray-900">
                              <HighlightedText
                                text={m.labelHumanized}
                                highlights={m.labelHighlights}
                              />
                            </span>
                            <span className="break-all text-xs text-gray-600">
                              <HighlightedText
                                text={m.valuePreview}
                                highlights={m.valueHighlights}
                              />
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
              <div className="border-t border-gray-100 bg-gray-50 px-4 py-2 text-[11px] text-gray-500">
                Searching loaded categories - {loadedCount} of {totalCount} loaded
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
